"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useMemo } from "react";
import { FiPackage, FiUsers } from "react-icons/fi";
import { useUser } from "@/contexts/user-provider";
import { categoryAncestorsByName } from "@/lib/category-tree";
import { isDiscountApplicable } from "@/lib/discount-manager";
import { formatMembershipName } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import type { DiscountDetail } from "@/types/billing/discount";
import { DiscountType } from "@/types/enums/discount";
import { ItemType } from "@/types/enums/item-type";
import { EventDetail, getServiceSegments } from "@/types/event";
import { BenefitUsageList } from "./sections/benefit-usage-list";
import { getPatientDiscounts } from "@/api/provider/billing/discount";
import { patientDiscountsQueryKey } from "@/api/provider/billing/discount-query";
import { categoryQueryOptions } from "@/query/inventory/category";
interface Props {
  patientId: string;
  event: EventDetail;
}
const PLAN_BENEFIT_TYPES = [DiscountType.PACKAGE, DiscountType.MEMBERSHIP];
interface BenefitSource {
  key: string;
  name: ReactNode;
  isPackage: boolean;
  benefits: DiscountDetail[];
}
function sourceOf(benefit: DiscountDetail): Omit<BenefitSource, "benefits"> {
  if (benefit.soldPackageId) {
    return {
      key: `package-${benefit.soldPackageId}`,
      name: benefit.soldPackage?.package?.name ?? "Package",
      isPackage: true
    };
  }
  return {
    key: `member-${benefit.memberId ?? ""}`,
    name: benefit.member ? formatMembershipName(benefit.member.membership) : "Membership",
    isPackage: false
  };
}

/**
 * The patient's package and membership benefits that would apply to this
 * visit's services, with what is left of each, so the front desk knows before
 * opening checkout whether this visit is redeemed or sold. Reads the same
 * discounts checkout loads and matches them the way checkout does.
 */
export function ChecklistPaymentBenefits(t0) {
  const $ = _c(24);
  const {
    patientId,
    event
  } = t0;
  const {
    currentUser
  } = useUser();
  let t1;
  if ($[0] !== currentUser) {
    t1 = hasPermission(currentUser, PERMISSIONS.BILLING_READ);
    $[0] = currentUser;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canReadBilling = t1;
  let t2;
  if ($[2] !== patientId) {
    t2 = patientDiscountsQueryKey(patientId);
    $[2] = patientId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== patientId) {
    t3 = () => getPatientDiscounts(patientId);
    $[4] = patientId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = !!patientId && canReadBilling;
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      select: _temp,
      enabled: t4
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    data: t6
  } = useQuery(t5);
  let t7;
  if ($[10] !== t6) {
    t7 = t6 === undefined ? [] : t6;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const discounts = t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = categoryQueryOptions.list();
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== canReadBilling) {
    t9 = {
      ...t8,
      enabled: canReadBilling
    };
    $[13] = canReadBilling;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const {
    data: categories
  } = useQuery(t9);
  let t10;
  if ($[15] !== categories || $[16] !== discounts || $[17] !== event.segments) {
    const ancestors = categoryAncestorsByName(categories ?? []);
    const services = getServiceSegments(event.segments ?? []).map(_temp2);
    const bySource = new Map();
    for (const discount of discounts) {
      if (!PLAN_BENEFIT_TYPES.includes(discount.type)) {
        continue;
      }
      if (!services.some(service => isDiscountApplicable(discount, service, ancestors))) {
        continue;
      }
      const source = sourceOf(discount);
      const entry = bySource.get(source.key) ?? {
        ...source,
        benefits: []
      };
      entry.benefits.push(discount);
      bySource.set(source.key, entry);
    }
    t10 = Array.from(bySource.values()).sort(_temp3);
    $[15] = categories;
    $[16] = discounts;
    $[17] = event.segments;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const sources = t10;
  if (!canReadBilling || sources.length === 0) {
    return null;
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <p className="text-caption font-medium text-foreground-secondary">Applies to this visit</p>;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== sources) {
    t12 = sources.map(_temp4);
    $[20] = sources;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12) {
    t13 = <div className="flex w-full flex-col gap-2 rounded-md border px-3 py-2">{t11}<ul className="flex flex-col gap-2">{t12}</ul></div>;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  return t13;
}
function _temp4(source_0) {
  const Icon = source_0.isPackage ? FiPackage : FiUsers;
  return <li key={source_0.key} className="min-w-0"><div className="flex min-w-0 items-center gap-1.5 text-caption-strong text-foreground"><Icon aria-hidden={true} className="h-3 w-3 shrink-0 text-foreground-secondary" /><span className="truncate">{source_0.name}</span></div><BenefitUsageList benefits={source_0.benefits} className="mt-1 space-y-0.5 pl-[18px]" /></li>;
}
function _temp3(a, b) {
  return Number(b.isPackage) - Number(a.isPackage);
}
function _temp2(segment) {
  return {
    id: segment.service.id,
    category: segment.service.category,
    type: ItemType.SERVICE
  };
}
function _temp(data) {
  return data ?? [];
}